<script setup lang="ts">
import { PosthogEvent } from '@/core/enums/posthog.enum';
import { useKeycloak } from '@nxtpeople/nxt-vue-keycloak';
import { logEventWithPosthog, useMediaQueries, USER_ENDPOINTS } from '@nxtpeople/nxt-styles';
import { Team } from '../../../core/collections/api/team/team.interface';

const { sm } = useMediaQueries();
const router = useRouter();
const route = useRoute();
const { state, commit, dispatch } = useStore();
const { decodedToken, keycloak } = useKeycloak();
const { t } = useI18n();

//#region Props & Emits
const emits = defineEmits(['onShowSettingsPanel']);
//#endregion

//#region Breadcrumbs
const breadcrumbs = computed(() => state.header?.breadcrumbs);
//#endregion

//#region User Info
const user_info = computed(() => state.user?.user_info);
const user_actions = computed(() => {
   const USER_ACTIONS: any = [
      { label: t('actions.sign_out'), iconClass: 'fa-solid fa-arrow-right-from-bracket', to: keycloak.createLogoutUrl() },
   ];

   if (isMobile.value) {
      USER_ACTIONS.unshift({ label: t('settings.settings'), iconClass: 'fa-solid fa-cog', onClick: () => emits('onShowSettingsPanel') });
      USER_ACTIONS.unshift({ label: 'FAQ', iconClass: 'fa-solid fa-circle-question', onClick: goToNotion });
   }

   return USER_ACTIONS;
});

//#endregion

// #region breadcrumb click
function breadcrumbClick(breadcrumb: { to: string }) {
   if (breadcrumb.to === '/board') {
      commit('space/SET', undefined);
   }
}
// #endregion

// #region notion
function goToNotion() {
   logEventWithPosthog(PosthogEvent.NxtPlanClickFAQ);
   window.open('https://nxtpeople.notion.site/FAQ-NxtPlan-9b5396085e484f92a667cb467c4eda67', '_blank');
}
// #endregion

//# region team
function handleChangeDefaultTeam(team: Team) {
   dispatch('user/patch', { path: 'default_team_id', value: team.id }).then((response) => {
      commit('user/UPDATE_DEFAULT_TEAM', response.data.default_team_id);
   });
}

async function changeProfilePicture(url: string | null = null) {
   if (!user_info.value || !url) return;

   const file = await fetch(url)
      .then((res) => res.blob())
      .then((blob) => new File([blob], 'profile_picture', { type: blob.type }));

   const formData = new FormData();
   formData.append('profile_picture', file);

   const response = await api.post(USER_ENDPOINTS.POST_PROFILE_PICTURE(user_info.value.sync_id || ''), formData, {});
   commit('user/PATCH_USER_INFO', { profile_picture_url: response.data.profile_picture_url });
}
//# endregion

// #region is mobile
const isMobile = inject('isMobile', ref(false));
const mobileTitle = computed(() => {
   const lastBreadcrumb = breadcrumbs.value?.[breadcrumbs.value.length - 1];
   return lastBreadcrumb ? lastBreadcrumb.content : '';
});
// #endregion
</script>

<template>
   <NxtPageHeader
      :user-info="user_info"
      :fallback-username="decodedToken?.preferred_username"
      :modules="user_info?.modules"
      :user-actions="user_actions"
      :class="['general-page-header', { '-sm': sm }]"
      :manage-organisation-text="t('actions.manage_organisation')"
      :has-team-switcher="true"
      :default_team_id="user_info?.default_team_id"
      :is-mobile="isMobile"
      :mobile-title
      :on-set-origin="
         () => {
            commit('space/SET', undefined);
            router.push('/');
         }
      "
      @change-default-team="(team: Team) => handleChangeDefaultTeam(team as Team)"
      @on-upload-picture="changeProfilePicture"
   >
      <template v-if="!isMobile" #breadcrumbs>
         <RouterLink
            v-for="(breadcrumb, index) in breadcrumbs"
            :key="index"
            v-bind="breadcrumb"
            :to="{
               path: breadcrumb.to,
               query: breadcrumb.to === '/' ? {} : route.query.board_id ? { board_id: route.query.board_id } : {},
            }"
            :title="breadcrumb.to"
            :class="{ active: index === (breadcrumbs?.length || 0) - 1 }"
            @click="breadcrumbClick(breadcrumb)"
         >
            {{ breadcrumb.content }}
         </RouterLink>
      </template>

      <template #pageActions>
         <div class="page-header__actions -desktop"></div>
         <div class="page-header__actions -mobile"></div>
      </template>

      <template v-if="!isMobile" #globalActions>
         <template v-if="user_info">
            <NxtIconButton class="notion" :name="'bug'" @on-click="router.push({ name: 'bug-report' })">
               <template #hover-text> {{ t('global.report_a_problem') }} </template>
            </NxtIconButton>
            <NxtIconButton class="notion" :name="'circle-question'" @on-click="goToNotion">
               <template #hover-text> FAQ </template>
            </NxtIconButton>
            <NxtIconButton :name="'cog'" :hover-text="$t('settings.settings')" @on-click="emits('onShowSettingsPanel')" />
         </template>
      </template>
   </NxtPageHeader>
</template>

<style lang="scss">
.general-page-header.-sm {
   .nxt-user-info__hover-item {
      border: unset;
      height: toRem(25);
   }

   .nxt-profile-picture__initials {
      max-width: toRem(25);
      max-height: toRem(25);
      font-size: var(--nxt-font-base) !important;
   }
}

.nxt-page-header__content {
   @include md {
      width: 100%;
   }
}
</style>
